import { publicApi } from "@api";
import GetCodeButton, { GetCodeButtonRef } from "@components/inputs/GetCodeButton/GetCodeButton";
import PasswordTextField from "@components/inputs/PasswordTextField/PasswordTextField";
import { usePostLoginCheck } from "@components/utils/Auth/hooks";
import { RegisterCallbackProps } from "@components/utils/Auth/types/auth-props";
import { envConfig } from "@configs/env";
import { ValidatorSchema } from "@constants";
import { zodResolver } from "@hookform/resolvers/zod";
import useContactLinks from "@hooks/useContactLinks";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { clearReferralCode, getReferralCode } from "@utils/referral";
import { resolveRegisterRequestError } from "@views/AuthLoginPage/utils/response";
import {
  APIError,
  APIErrorCodes,
  APIValidationError,
  Button,
  ErrorResponse,
  QueryError,
  TFunction,
  TextField,
  standardizePhoneNumber,
  useHandleApiResponse,
} from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MdMail, MdPerson, MdPhoneAndroid } from "react-icons/md";
import { z } from "zod";

export type RegisterFormType = {
  username: string;
  password: string;
  phone: string;
  sms: string;
};

const inputClasses = {
  "input&": "w-full !px-0 !pl-11 text-start",
  "iconContainer&": "!left-3 !top-[10px]",
};

const isValidationError = (error: APIError): error is APIValidationError => {
  return error.message === APIErrorCodes.ValidationError;
};

const getErrorMessages = (error: QueryError<ErrorResponse>, t: TFunction) => {
  const errorData = error?.data?.error;
  if (errorData && isValidationError(errorData)) {
    return {
      username: t(errorData?.errors?.accessKey?.msg),
      phone: t(errorData?.errors?.phone?.msg),
    };
  }
  return {};
};

interface RegisterUPProps extends RegisterCallbackProps {}

const RegisterUP = ({
  onRegisterSuccess,
  onLoginSuccess,
  onPostLoginCheckFailed,
  onPostLoginCheckPassed,
  deferUpdateToken,
}: RegisterUPProps) => {
  const { phoneLength, phonePrefix } = useMemberSettings();
  const { t } = useTranslation();
  const getCodeButtonRef = useRef<GetCodeButtonRef>(null);
  const postLoginCheck = usePostLoginCheck({
    onLoginSuccess,
    onPostLoginCheckPassed,
    onPostLoginCheckFailed,
    redirectHome: false,
  });
  const [createRegisterMutation, { isLoading: isCreateRegisterLoading }] = publicApi.useCreateRegisterMutation();
  const [verifyOTP, { isLoading: isVerifyingOTP }] = publicApi.useVerifyOTPMutation();
  const [register, { isLoading: isRegistering }] = publicApi.useSubmitRegisterMutation();
  const { handleLogin, isLoading: isLoginLoading } = useLoginSubmit({
    onLoginSuccess: postLoginCheck,
    deferUpdateToken,
  });

  const { openContact } = useContactLinks();

  const handleOpenContactLink = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      openContact();
    },
    [openContact],
  );

  const registerFormSchema = useMemo(() => {
    return z.object({
      username: ValidatorSchema.username,
      password: ValidatorSchema.registerPassword,
      phone: ValidatorSchema.phone[phoneLength],
      sms: ValidatorSchema.otp,
    });
  }, [phoneLength]);

  const {
    handleSubmit: handleFormSubmit,
    register: formRegister,
    formState: { errors: formErrors },
    trigger,
    getValues,
    setError,
  } = useForm<RegisterFormType>({
    mode: "onChange",
    resolver: zodResolver(registerFormSchema),
  });

  const { handleApiResponse } = useHandleApiResponse({ toast });

  const handleGetCode = async () => {
    return new Promise((resolve, reject) => {
      trigger(["username", "password", "phone"]).then((isFormValid) => {
        if (!isFormValid) {
          reject(new Error("Form is not valid"));
          return;
        }

        const { phone, username, password } = getValues();
        createRegisterMutation({
          phone: standardizePhoneNumber(phone, phonePrefix),
          accountId: envConfig.accountId,
          accessKey: username.toLowerCase(),
          password,
        }).then((response) => {
          handleApiResponse(response, {
            successMessage: t("SMS code sent"),
            toastError: false,
            onSuccess: (otpRes) => {
              const nextRequestAt = otpRes.nextRequestAt;
              const remainingSeconds = moment(nextRequestAt).diff(moment(), "seconds");
              getCodeButtonRef.current?.startTimer(remainingSeconds);
              resolve(otpRes);
            },
            onError: (error) => {
              const message = resolveRegisterRequestError(error, t);
              if (message) {
                toast.error(message);
              }
              reject(error);
            },
          });
        });
      });
    });
  };

  const handleOnSubmit = async (data: RegisterFormType) => {
    const { phone, sms: code, password, username } = data;
    const otpRes = await verifyOTP({
      phone: standardizePhoneNumber(phone, phonePrefix),
      code: code,
      accountId: envConfig.accountId,
    });

    handleApiResponse(otpRes, {
      toastError: false,
      toastSuccess: false,
      onError: (error) => {
        if (error?.data?.error?.message) {
          setError("sms", { message: t(error?.data?.error.message) });
        }
      },
    });

    if (otpRes?.error) return;

    const referralCode = getReferralCode();
    const registerRes = await register({
      accountId: envConfig.accountId,
      phone: standardizePhoneNumber(phone, phonePrefix),
      password,
      accessKey: username.toLowerCase(),
      code,
      referralCode,
    });

    handleApiResponse(registerRes, {
      toastSuccess: false,
      toastError: false,
      onSuccess: () => {
        toast.success(t("Register successfully!"));
        if (referralCode) {
          clearReferralCode();
        }
        handleLogin(data.username, data.password);
        onRegisterSuccess?.();
      },
      onError: (error) => {
        const errorMessages = getErrorMessages(error, t);
        if (errorMessages.username) {
          setError("username", { message: errorMessages.username });
        }
        if (errorMessages.phone) {
          setError("phone", { message: errorMessages.phone });
        }
        if (!errorMessages.username && !errorMessages.phone && error?.data?.error?.message) {
          toast.error(error?.data?.error?.message);
        }
      },
    });
  };

  const isLoading = isLoginLoading || isVerifyingOTP || isRegistering;

  return (
    <form
      onSubmit={handleFormSubmit(handleOnSubmit)}
      className="flex w-full flex-col gap-y-3"
      autoComplete="off"
    >
      <TextField
        {...formRegister("username")}
        error={!!formErrors.username}
        helperText={t(formErrors.username?.message as string)}
        placeholder={t("Username")}
        icon={<MdPerson className="text-2xl text-content-base" />}
        classes={inputClasses}
        size="sm"
        autoComplete="new-password"
      />
      <PasswordTextField
        {...formRegister("password")}
        error={!!formErrors.password}
        helperText={t(formErrors.password?.message as string)}
        placeholder={t("Password")}
        classes={inputClasses}
        buttonClass="!top-[10px]"
        size="sm"
        autoComplete="new-password"
      />
      <TextField
        {...formRegister("phone")}
        error={!!formErrors.phone}
        helperText={t(formErrors.phone?.message as string)}
        placeholder={t("Phone number")}
        type="number"
        inputMode="numeric"
        icon={<MdPhoneAndroid className="text-2xl text-content-base" />}
        classes={inputClasses}
        size="sm"
        autoComplete="new-password"
      />
      <div className="relative">
        <TextField
          {...formRegister("sms")}
          error={!!formErrors.sms}
          helperText={t(formErrors.sms?.message as string)}
          placeholder={t("enter code")}
          type="text"
          icon={<MdMail className="text-2xl text-content-base" />}
          classes={inputClasses}
          size="sm"
          autoComplete="one-time-code"
          helperTextClasses={{
            classes: {
              "errorText&": "!h-4",
            },
          }}
        />
        <GetCodeButton
          onGetCode={handleGetCode}
          className="absolute right-3 top-2.5"
          classes={{
            "root&": "w-fit px-2 !h-6",
          }}
          loading={isCreateRegisterLoading}
          ref={getCodeButtonRef}
        />
        {formErrors.sms?.message === "otp is required. click on get code" && (
          <div className="text-right text-xs text-error-base">
            {t("didnt receive")}{" "}
            <button
              onClick={handleOpenContactLink}
              className="underline"
            >
              {t("contact cs")}
            </button>
          </div>
        )}
      </div>
      <Button
        variant="primary"
        type="submit"
        loading={isLoading}
        disabled={isLoading}
        size="lg"
        className="mt-2"
      >
        {t("Next")}
      </Button>
    </form>
  );
};

export default RegisterUP;
